import { formatWritingCompanyLabel } from '@pages/PortalAdmin/FormsV2Admin/FormCatalog/writingCompanyUtils';
import { writingCompanyIds } from '@models/productOffering';

/**
 * LIO-29798: Shared display helpers for the LSIC issuing-company override on
 * Quotes/Policies list views. The *displayed* writing company is the issuing
 * override when one is set, otherwise the record's own writing company.
 *
 * Display and filtering both follow the override: the `writingCompanyId` query
 * param is unchanged, but the API resolves it against the effective company, so
 * an overridden LIC quote matches a filter for LSIC and not one for LIC.
 * Sorting is the exception — it stays on the underlying `writingCompanyId`, so an
 * overridden record sorts among LIC rows while displaying and filtering as LSIC.
 * That is intended; see LIO-29798 requirement 4.4.
 */
/**
 * The carrier a quote is actually issued on: the issuing override when set, otherwise the
 * quote's own writing company. Use this anywhere behavior follows the *paper* — billing,
 * payment, carrier identity on documents. Do NOT use it for rating, coverages, or form
 * selection, which deliberately stay on `writingCompany` even when the override is applied.
 */
export const getEffectiveWritingCompanyId = (quote?: {
  writingCompany?: { id: string } | null;
  issuingWritingCompany?: { id: string } | null;
}): string | undefined => quote?.issuingWritingCompany?.id ?? quote?.writingCompany?.id;

export const getDisplayedWritingCompanyLabel = ({
  writingCompanyName,
  issuingWritingCompanyId,
}: {
  writingCompanyName?: string | null;
  issuingWritingCompanyId?: string | null;
}): string => {
  if (issuingWritingCompanyId) {
    return issuingWritingCompanyId === writingCompanyIds.specialty ? 'LSIC' : 'LIC';
  }
  return formatWritingCompanyLabel(writingCompanyName, '-');
};

export const ISSUING_COMPANY_OVERRIDE_TOOLTIP =
  'Set by an underwriter override — this quote is issued on LSIC paper, but continues to rate, apply ' +
  'coverages, and select forms as LIC.';

export const getWritingCompanyTooltip = (issuingWritingCompanyId?: string | null): string | undefined =>
  issuingWritingCompanyId ? ISSUING_COMPANY_OVERRIDE_TOOLTIP : undefined;
